import { Controller } from "@hotwired/stimulus"

const ROW_SELECTOR = "tbody tr[data-lead-id]"
const SELECTED_CLASS = "last-opened-lead"
const WIDE_QUERY = "(min-width: 1536px)"
const LOAD_DELAY = 150
const ROW_CHECKBOX_SELECTOR = "input[type='checkbox'][name='id[]']"
// Text fields and widgets that keep their own keys; a focused checkbox does not. Kept in sync with the keyboard shortcuts controller.
const TYPING_SELECTOR = "input:not([type='checkbox']):not([type='radio']):not([type='button']):not([type='submit']):not([type='reset']), textarea, select, [contenteditable]:not([contenteditable='false']), [role='textbox'], [role='combobox'], [role='listbox'], [role='menu']"
// Open menus and popovers get Escape for themselves. The app's dropdown menus show by dropping .hidden; popover menus
// such as the bulk actions More menu stay in the page while closed, so only :popover-open counts for them.
const OPEN_MENU_SELECTOR = "[role='menu']:not(.hidden):not([popover])"

function popupOpen() {
  if (document.querySelector(OPEN_MENU_SELECTOR)) return true
  try {
    return Boolean(document.querySelector(":popover-open"))
  } catch {
    // Browsers without the Popover API reject the selector and have no open popovers.
    return false
  }
}

// Owns the selection of the lead list. A click selects one lead, Cmd/Ctrl-click adds or removes one, Shift-click selects a
// range, a checkbox adds or removes its lead, Cmd/Ctrl+A selects the page and Escape clears. j/k and the arrow keys move a
// single selection. One selected lead shows in the sidebar; several show as a list with the bulk actions in the same place.
// A click in the sidebar chat hands the arrow keys and Cmd/Ctrl+A to it until the next click anywhere else; j and k always
// work. The sidebar only shows with a selection, and Decisions opens its first lead on a wide screen without ticking it, so
// reading it does not add it to a later selection. The open state lives in a cookie so the server renders it without a
// layout jump. Below 1536px the sidebar is a drawer over the table; a first visit there starts with it closed.
// A lead can also be open without a row in the list: from a link to it (?lead=) or from a referral in the sidebar.
export default class extends Controller {
  static targets = ["panel", "scroller", "chat", "leadName", "position", "tabs", "tab", "previousButton", "nextButton", "resizeHandle", "decisionCount", "header", "bulkPanel", "bulkTitle", "bulkList", "bulkNotice"]
  static values = { cookie: String, selectFirst: Boolean, focusLead: String, focusLeadUrl: String }

  connect() {
    this.keydown = event => this.handleKey(event)
    this.click = event => this.handleClick(event)
    this.pointerDown = event => this.trackPointer(event)
    this.morphed = () => this.refreshed()
    this.arrowKeys = true
    this.drafts = new Map()
    this.activeTab = "decision"
    this.input = () => this.saveDrafts()
    this.contentObserver = new MutationObserver(() => this.renderTabs())
    this.contentObserver.observe(this.chatTarget, { childList: true, subtree: true })
    this.submitting = event => this.trackSubmission(event)
    this.selectionChanged = event => this.applySelection(event)
    this.mouseDown = event => this.preventRangeTextSelection(event)
    document.addEventListener("keydown", this.keydown)
    document.addEventListener("pointerdown", this.pointerDown)
    document.addEventListener("turbo:morph", this.morphed)
    this.element.addEventListener("click", this.click)
    this.element.addEventListener("input", this.input)
    this.element.addEventListener("change", this.input)
    this.element.addEventListener("turbo:submit-start", this.submitting)
    this.element.addEventListener("list:selection-changed", this.selectionChanged)
    this.element.addEventListener("mousedown", this.mouseDown)
    this.selectedId = this.element.querySelector(`${ROW_SELECTOR}.${SELECTED_CLASS}`)?.dataset.leadId || null
    if (!this.isWide() && !this.hasStoredState()) this.panelTarget.removeAttribute("data-open")
    this.panelTarget.removeAttribute("data-unset")
    const width = Number(document.cookie.split("; ").find(entry => entry.startsWith("lead_sidebar_width="))?.split("=")[1])
    if (width) this.setWidth(width)
    if (this.focusLeadValue) this.focusLinkedLead()
    else if (this.selectFirstValue && !this.selectedId && this.isOpen() && this.isWide()) this.move(1)
    else this.render()
  }

  // Opens the lead a link pointed at, then drops ?lead= from the address so later redirects back here, such as after a
  // decision, do not reopen it.
  focusLinkedLead() {
    this.open(this.focusLeadValue, this.focusLeadUrlValue)
    this.rowFor(this.selectedId)?.scrollIntoView?.({ block: "nearest" })
    const url = new URL(window.location.href)
    url.searchParams.delete("lead")
    window.history.replaceState(window.history.state, "", url)
  }

  // Selects a lead whether or not the list shows it. Without a row the sidebar loads it from its URL.
  open(id, url) {
    const row = this.rowFor(id)
    this.detached = row ? null : { id, url }
    this.setTicked(row ? [row] : [])
    this.select(id)
  }

  disconnect() {
    this.stopResize()
    this.contentObserver.disconnect()
    clearTimeout(this.loadTimer)
    document.removeEventListener("keydown", this.keydown)
    document.removeEventListener("pointerdown", this.pointerDown)
    document.removeEventListener("turbo:morph", this.morphed)
    this.element.removeEventListener("click", this.click)
    this.element.removeEventListener("input", this.input)
    this.element.removeEventListener("change", this.input)
    this.element.removeEventListener("turbo:submit-start", this.submitting)
    this.element.removeEventListener("list:selection-changed", this.selectionChanged)
    this.element.removeEventListener("mousedown", this.mouseDown)
  }

  rows() {
    return Array.from(this.element.querySelectorAll(ROW_SELECTOR))
  }

  rowFor(id) {
    return id ? this.rows().find(row => row.dataset.leadId === id) : null
  }

  isWide() {
    return window.matchMedia(WIDE_QUERY).matches
  }

  isOpen() {
    return this.panelTarget.hasAttribute("data-open")
  }

  hasStoredState() {
    return document.cookie.split("; ").some(entry => entry.startsWith(`${this.cookieValue}=`))
  }

  // Closing clears the selection, because a selection without the sidebar would have nowhere to show its actions.
  toggle() {
    const open = this.panelTarget.toggleAttribute("data-open")
    document.cookie = `${this.cookieValue}=${open ? "open" : "closed"}; path=/; max-age=31536000; SameSite=Lax`
    // There is no empty state, so opening without a selection starts at the top row.
    if (open && !this.selectedId) return this.move(1)
    if (!open) this.setTicked([])
    this.render()
  }

  clearSelection() {
    this.setTicked([])
  }

  previous() { this.move(-1) }

  next() { this.move(1) }

  setWidth(width) {
    const maximum = Math.min(832, window.innerWidth - 32)
    const value = Math.round(Math.max(Math.min(360, maximum), Math.min(maximum, width)))
    this.panelTarget.style.setProperty("--lead-sidebar-width", `${value}px`)
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.setAttribute("aria-valuenow", String(value))
      this.resizeHandleTarget.setAttribute("aria-valuemax", String(maximum))
    }
    document.cookie = `lead_sidebar_width=${value}; path=/; max-age=31536000; SameSite=Lax`
  }

  startResize(event) {
    if (event.button !== 0) return
    event.preventDefault()
    const startWidth = this.panelTarget.getBoundingClientRect().width
    const startX = event.clientX
    this.resizing = event => this.setWidth(startWidth + startX - event.clientX)
    this.resizeEnd = () => this.stopResize()
    window.addEventListener("pointermove", this.resizing)
    window.addEventListener("pointerup", this.resizeEnd)
    window.addEventListener("pointercancel", this.resizeEnd)
  }

  stopResize() {
    if (!this.resizing) return
    window.removeEventListener("pointermove", this.resizing)
    window.removeEventListener("pointerup", this.resizeEnd)
    window.removeEventListener("pointercancel", this.resizeEnd)
    this.resizing = null
  }

  resizeKeydown(event) {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return
    event.preventDefault()
    this.setWidth(this.panelTarget.getBoundingClientRect().width + (event.key === "ArrowLeft" ? 32 : -32))
  }

  chooseTab(event) {
    this.activeTab = event.currentTarget.dataset.tab
    this.renderTabs()
  }

  tabKeydown(event) {
    const tabs = this.tabTargets
    const index = tabs.indexOf(event.currentTarget)
    let next
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length
    else if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = tabs.length - 1
    else return
    event.preventDefault()
    this.activeTab = tabs[next].dataset.tab
    this.renderTabs()
    tabs[next].focus()
  }

  renderTabs() {
    for (const tab of this.tabTargets || []) {
      const selected = tab.dataset.tab === this.activeTab
      tab.setAttribute("aria-selected", String(selected))
      tab.tabIndex = selected ? 0 : -1
    }
    this.chatTarget.querySelectorAll("[data-sidebar-tab-panel]").forEach(panel => {
      panel.hidden = panel.dataset.sidebarTabPanel !== this.activeTab
    })
    if (this.hasDecisionCountTarget) {
      this.decisionCountTarget.textContent = String(this.chatTarget.querySelectorAll("[data-sidebar-approval], [data-referral-decision], [data-sidebar-followup]").length)
    }
    if (this.pendingHistoryScroll && this.conversationVisible()) this.scrollToLatest()
  }

  // The Comment action opens the composer below the existing comments.
  toggleComposer() {
    const composer = this.chatTarget.querySelector("[data-sidebar-composer]")
    if (!composer) return
    composer.hidden = !composer.hidden
    if (!composer.hidden) composer.querySelector("textarea")?.focus()
  }

  // An unfinished comment that was restored stays visible instead of waiting behind the Comment action.
  showComposerWithDraft() {
    const composer = this.chatTarget.querySelector("[data-sidebar-composer]")
    if (composer?.querySelector("textarea")?.value.trim()) composer.hidden = false
  }

  trackPointer(event) {
    this.arrowKeys = !this.scrollerTarget.contains(event.target)
  }

  handleClick(event) {
    if (event.button !== 0) return
    const row = event.target.closest(ROW_SELECTOR)
    if (event.target.closest("input[type=checkbox]")) {
      if (!row) return
      if (event.shiftKey) this.selectRange(row)
      this.anchor = row
      return
    }
    if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
      if (row) this.handleModifiedRowClick(event, row)
      return
    }
    const leadLink = event.target.closest("a[data-sidebar-lead-id]")
    if (leadLink && this.panelTarget.contains(leadLink)) {
      event.preventDefault()
      this.open(leadLink.dataset.sidebarLeadId, leadLink.dataset.sidebarLeadUrl)
      return
    }
    if (event.target.closest("[data-sidebar-toggle-composer]")) {
      this.toggleComposer()
      return
    }
    if (event.target.closest("[data-bulk-remove]")) {
      this.removeFromSelection(event.target.closest("[data-bulk-remove]").dataset.bulkRemove)
      return
    }
    if (!row || this.isOwnControl(event, row)) return
    if (event.target.closest("a")) event.preventDefault()
    this.anchor = row
    this.selectOnly(row)
  }

  // Links to somewhere else and the row's own controls keep working without touching the selection.
  isOwnControl(event, row) {
    const control = event.target.closest("a, button, select, input, label, [role='menu']")
    return Boolean(control) && control.getAttribute?.("href") !== row.dataset.leadUrl
  }

  handleModifiedRowClick(event, row) {
    if (event.altKey || this.isOwnControl(event, row)) return
    event.preventDefault()
    if (event.shiftKey) {
      this.selectRange(row)
    } else {
      const checkbox = this.checkboxFor(row)
      if (checkbox) checkbox.checked = !checkbox.checked
      this.notifySelection()
      this.anchor = row
    }
  }

  // Shift-click should select rows, not the text between them.
  preventRangeTextSelection(event) {
    if (event.shiftKey && event.target.closest?.(ROW_SELECTOR)) event.preventDefault()
  }

  checkboxFor(row) {
    return row.querySelector?.(ROW_CHECKBOX_SELECTOR) || null
  }

  tickedRows() {
    return this.rows().filter(row => this.checkboxFor(row)?.checked)
  }

  // Ticks exactly these rows. The list controller keeps its counts and tells this controller through list:selection-changed.
  setTicked(rows) {
    this.rows().forEach(row => {
      const checkbox = this.checkboxFor(row)
      if (checkbox) checkbox.checked = rows.includes(row)
    })
    this.notifySelection()
  }

  notifySelection() {
    const checkbox = this.rows().map(row => this.checkboxFor(row)).find(Boolean)
    if (checkbox) checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    else this.applySelection()
  }

  selectOnly(row) {
    this.setTicked([row])
    this.select(row.dataset.leadId)
  }

  // From the last clicked lead to this one, like every list with Shift-click. Without one, the range starts at the top.
  selectRange(row) {
    const rows = this.rows()
    const from = Math.max(rows.indexOf(this.anchor), 0)
    const to = rows.indexOf(row)
    this.setTicked(rows.slice(Math.min(from, to), Math.max(from, to) + 1))
  }

  removeFromSelection(id) {
    const checkbox = this.checkboxFor(this.rowFor(id))
    if (checkbox) checkbox.checked = false
    this.notifySelection()
  }

  selectAll() {
    this.setTicked(this.rows())
    this.anchor = this.rows()[0]
  }

  // Follows the ticks: none clears a ticked selection, one shows that lead, several show the bulk view. A lead opened
  // without a tick stays open, and a morph refresh is settled by refreshed() instead.
  applySelection(event) {
    if (event?.detail?.morph) return
    const ticked = this.tickedRows()
    const hadTicks = this.hadTicks
    this.hadTicks = ticked.length > 0
    this.bulk = ticked.length > 1
    if (this.bulk) {
      this.selectedId = null
      this.detached = null
      this.panelTarget.setAttribute("data-open", "")
      this.render()
    } else if (ticked.length === 1) {
      if (this.selectedId === ticked[0].dataset.leadId) this.render()
      else this.select(ticked[0].dataset.leadId)
    } else if (hadTicks) {
      this.select(null)
    } else {
      this.render()
    }
  }

  handleKey(event) {
    if (this.isSelectAll(event)) {
      event.preventDefault()
      this.selectAll()
      return
    }
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    if (event.target.isContentEditable || event.target.closest?.(TYPING_SELECTOR)) return
    if (document.querySelector("dialog[open]")) return

    const action = this.actionFor(event)
    if (!action) return
    event.preventDefault()
    action()
  }

  // Text fields, open dialogs and a chat the user clicked into keep Cmd/Ctrl+A for selecting text.
  isSelectAll(event) {
    if (event.defaultPrevented || event.altKey || event.shiftKey || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "a") return false
    if (event.target.isContentEditable || event.target.closest?.(TYPING_SELECTOR)) return false
    return this.arrowKeys && !document.querySelector("dialog[open]")
  }

  actionFor(event) {
    if (event.shiftKey) return null
    switch (event.key) {
      case "ArrowDown":
        return this.arrowKeys ? () => this.move(1) : null
      case "j":
        return () => this.move(1)
      case "ArrowUp":
        return this.arrowKeys ? () => this.move(-1) : null
      case "k":
        return () => this.move(-1)
      case "s":
        return () => this.toggle()
      case "Escape":
        if (popupOpen()) return null
        if (this.isOpen()) return () => this.toggle()
        return this.selectedId ? () => this.select(null) : null
      default:
        return null
    }
  }

  // Without a selection both directions start at the top row.
  move(direction) {
    const rows = this.rows()
    if (rows.length === 0) return
    const index = rows.indexOf(this.rowFor(this.selectedId))
    const next = index === -1 ? rows[0] : rows[Math.max(0, Math.min(rows.length - 1, index + direction))]
    if (document.activeElement && next.closest("table")?.contains(document.activeElement)) document.activeElement.blur()
    this.anchor = next
    this.moveSelection(next)
    const header = next.closest("table")?.tHead
    next.style.scrollMarginTop = `${(header?.offsetHeight || 0) + 16}px`
    next.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  // Decisions opens its first lead without ticking it. Moving on from there, or anywhere else, is a click on the next row.
  moveSelection(row) {
    if (this.selectFirstValue && !this.selectedId && !this.hadTicks) this.select(row.dataset.leadId)
    else this.selectOnly(row)
  }

  // A morph refresh (bulk actions redirect back to this page) resets the sidebar
  // and rows to the server HTML but keeps this controller, so draw it again.
  refreshed() {
    clearTimeout(this.loadTimer)
    this.loadedId = null
    if (this.selectedId && !this.rowFor(this.selectedId) && !this.detachedLead()) {
      const rows = this.rows()
      this.selectedId = rows[Math.min(this.selectedIndex || 0, rows.length - 1)]?.dataset.leadId || null
    }
    const ticked = this.tickedRows()
    this.bulk = ticked.length > 1
    this.hadTicks = ticked.length > 0
    this.render()
  }

  select(id) {
    if (id && !this.isOpen()) {
      this.panelTarget.setAttribute("data-open", "")
      document.cookie = `${this.cookieValue}=open; path=/; max-age=31536000; SameSite=Lax`
    }
    this.saveDrafts()
    this.selectedId = id
    this.render()
  }

  draftKey(form, id = this.selectedId) {
    return `${id}:${form.getAttribute("action")}`
  }

  saveDrafts() {
    if (!this.selectedId || this.loadedId !== this.selectedId) return
    this.drafts ||= new Map()
    this.chatTarget.querySelectorAll("form").forEach(form => {
      const fields = Array.from(form.elements).filter(field => field.name && !["hidden", "submit", "button"].includes(field.type))
      if (fields.length) this.drafts.set(this.draftKey(form), fields.map(field => ({ name: field.name, value: field.value, checked: field.checked })))
    })
  }

  restoreDrafts() {
    const forms = Array.from(this.chatTarget.querySelectorAll("form"))
    const keys = new Set(forms.map(form => this.draftKey(form)))
    for (const key of this.drafts?.keys() || []) {
      if (key.startsWith(`${this.selectedId}:`) && !keys.has(key)) this.drafts.delete(key)
    }
    forms.forEach(form => {
      const draft = this.drafts?.get(this.draftKey(form))
      if (!draft) return
      draft.forEach(({ name, value, checked }) => {
        const field = form.elements.namedItem(name)
        if (!field) return
        field.value = value
        if (["checkbox", "radio"].includes(field.type)) field.checked = checked
        field.dispatchEvent(new Event("change", { bubbles: true }))
      })
    })
  }

  trackSubmission(event) {
    if (!this.panelTarget.contains(event.target)) return
    const form = event.target
    const key = this.draftKey(form)
    form.addEventListener("turbo:submit-end", event => {
      // Redirects can also report a failed referral action via flash. Keep those inputs until the card disappears.
      if (event.detail.success && !event.detail.fetchResponse?.redirected) this.drafts?.delete(key)
    }, { once: true })
  }

  render() {
    const ticked = this.tickedRows()
    this.rows().forEach(row => row.classList.toggle(SELECTED_CLASS, row.dataset.leadId === this.selectedId || ticked.includes(row)))
    this.showBulk(ticked)
    if (this.bulk) {
      clearTimeout(this.loadTimer)
      this.chatTarget.replaceChildren()
      this.loadedId = null
      return
    }

    const row = this.rowFor(this.selectedId)
    if (row) {
      this.selectedIndex = this.rows().indexOf(row)
      this.detached = null
    }
    const lead = row ? row.dataset : this.detachedLead()
    this.panelTarget.toggleAttribute("data-selected", Boolean(lead))
    if (this.hasPreviousButtonTarget) this.previousButtonTarget.disabled = !row || this.selectedIndex === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !row || this.selectedIndex === this.rows().length - 1
    if (this.hasPositionTarget) {
      this.positionTarget.hidden = !row
      if (row) this.positionTarget.textContent = this.positionTarget.dataset.label.replace("__current__", this.selectedIndex + 1).replace("__total__", this.rows().length)
    }
    if (this.hasLeadNameTarget) this.leadNameTarget.hidden = Boolean(row) || !lead
    if (this.hasTabsTarget) this.tabsTarget.hidden = !lead
    this.renderTabs()
    if (!lead) {
      clearTimeout(this.loadTimer)
      this.chatTarget.replaceChildren()
      this.loadedId = null
      return
    }

    if (!this.isOpen()) this.cancelPendingLoad()
    else if (this.loadedId !== lead.leadId) this.scheduleLoad(lead)
  }

  showBulk(ticked) {
    const bulk = Boolean(this.bulk)
    this.panelTarget.toggleAttribute("data-bulk", bulk)
    if (bulk) this.panelTarget.toggleAttribute("data-selected", true)
    if (this.hasHeaderTarget) this.headerTarget.hidden = bulk
    if (this.hasTabsTarget && bulk) this.tabsTarget.hidden = true
    this.scrollerTarget.hidden = bulk
    if (!this.hasBulkPanelTarget) return
    this.bulkPanelTarget.hidden = !bulk
    if (!bulk) return
    this.bulkTitleTarget.textContent = this.bulkTitleTarget.dataset.label.replace("__count__", ticked.length)
    this.bulkNoticeTarget.textContent = this.bulkNoticeTarget.dataset.label.replace("__count__", ticked.length)
    this.renderBulkList(ticked)
  }

  // One line per lead, grouped by status, so a lead selected by accident stands out even among fifty.
  renderBulkList(ticked) {
    const groups = new Map()
    ticked.forEach(row => {
      const status = row.dataset.leadStatus
      if (!groups.has(status)) groups.set(status, [])
      groups.get(status).push(row)
    })
    const nodes = []
    for (const [status, rows] of groups) {
      const heading = document.createElement("h3")
      heading.className = "sticky top-0 flex items-center gap-1.5 bg-white pb-1 pt-3 text-xs font-semibold text-gray-500"
      const dot = document.createElement("span")
      dot.className = "size-1.5 shrink-0 rounded-full"
      dot.style.backgroundColor = rows[0].dataset.leadStatusColor
      heading.append(dot, `${status} (${rows.length})`)
      nodes.push(heading, ...rows.map(row => this.bulkListItem(row)))
    }
    this.bulkListTarget.replaceChildren(...nodes)
  }

  bulkListItem(row) {
    const { leadId, leadName } = row.dataset
    const item = document.createElement("div")
    item.className = "flex items-center justify-between gap-2 rounded px-1 py-0.5 hover:bg-gray-50"
    const name = document.createElement("span")
    name.className = "min-w-0 truncate text-gray-900"
    name.textContent = leadName
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "shrink-0 rounded px-1 text-gray-400 hover:text-gray-900 cursor-pointer"
    remove.textContent = "×"
    remove.dataset.bulkRemove = leadId
    remove.setAttribute("aria-label", this.bulkListTarget.dataset.removeLabel.replace("__name__", leadName))
    item.append(name, remove)
    return item
  }

  // A detached lead has no row to read from.
  detachedLead() {
    if (!this.detached || this.detached.id !== this.selectedId) return null
    return { leadId: this.detached.id, leadChatUrl: this.detached.url }
  }

  // A lead without a row is named by its loaded content.
  showLoadedLead(frame) {
    const name = frame.querySelector("template[data-sidebar-lead]")?.dataset.name
    if (name && this.hasLeadNameTarget) this.leadNameTarget.textContent = name
  }

  // Waits briefly so holding j only loads the chat of the lead the selection stops on.
  scheduleLoad(lead) {
    clearTimeout(this.loadTimer)
    this.loadedId = lead.leadId
    this.chatTarget.replaceChildren(this.loadingIndicator())
    this.loadTimer = setTimeout(() => this.load(lead.leadId, lead.leadChatUrl), LOAD_DELAY)
  }

  // Closing the sidebar before a scheduled load fires skips it; opening again schedules a new one.
  cancelPendingLoad() {
    if (!this.loadTimer) return
    clearTimeout(this.loadTimer)
    this.loadTimer = null
    this.loadedId = null
  }

  load(id, url) {
    this.loadTimer = null
    const frame = document.createElement("turbo-frame")
    frame.id = `lead-history-chat-${id}`
    frame.addEventListener("turbo:frame-load", event => {
      if (event.target !== frame || id !== this.selectedId) return
      this.showLoadedLead(frame)
      this.restoreDrafts()
      this.showComposerWithDraft()
      this.renderTabs()
      this.scrollToLatest()
    })
    frame.src = url
    this.chatTarget.replaceChildren(frame)
  }

  loadingIndicator() {
    const indicator = document.createElement("p")
    indicator.className = "py-8 text-center text-gray-400"
    indicator.textContent = "…"
    return indicator
  }

  conversationVisible() {
    return this.activeTab === "decision"
  }

  scrollToLatest() {
    this.pendingHistoryScroll = true
    if (!this.conversationVisible()) return
    const history = this.chatTarget.querySelector("[data-sidebar-history]")
    if (!history) return
    history.scrollTop = history.scrollHeight
    this.pendingHistoryScroll = false
  }
}
